import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import { RenderSecure } from "../../util/script/RenderSecure";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const Invoices = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const defaultType = searchParams.get("type") || "outgoing";

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [filterType, setFilterType] = useState(defaultType);
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => { fetchFinancialYears(); }, []);
  useEffect(() => { if (selectedFY) fetchInvoices(); }, [selectedFY, filterType, filterStatus]);

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      setFinancialYears(res.details || []);
      const cur = (res.details || []).find((y) => y.is_current === 1);
      setSelectedFY(String(cur?.id || res.details?.[0]?.id || ""));
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    const res = await ajaxAccounting.listInvoices({ type: filterType || undefined, status: filterStatus || undefined, financial_year_id: parseInt(selectedFY) });
    if (res.status === "OK") setInvoices(res.details?.list || (Array.isArray(res.details) ? res.details : []));
    else toast.error(res.message || "Failed to fetch invoices");
    setLoading(false);
  };

  const handleApprove = async (id) => {
    const res = await ajaxAccounting.approveInvoice({ id });
    if (res.status === "OK") { toast.success("Invoice approved!"); fetchInvoices(); }
    else toast.error(res.message);
  };

  const handleVoid = async (id) => {
    const reason = window.prompt("Reason for voiding this invoice:");
    if (!reason) return;
    const res = await ajaxAccounting.voidInvoice({ id, reason });
    if (res.status === "OK") { toast.success("Invoice voided."); fetchInvoices(); }
    else toast.error(res.message);
  };

  const statusBadge = (status) => {
    const map = { draft: "secondary", approved: "info", partial: "warning", paid: "success", overdue: "danger", void: "dark" };
    return <span className={`badge bg-${map[status] || "secondary"}`}>{status}</span>;
  };

  const columns = [
    { key: "invoice_number", label: "Invoice #", render: (v, row) => <strong className="tx-primary">{v || `INV-${row.id}`}</strong> },
    { key: "contact_name", label: filterType === "outgoing" ? "Customer" : "Supplier" },
    { key: "invoice_date", label: "Date", render: (v) => functions.formatDate(v) },
    { key: "due_date", label: "Due", render: (v, row) => {
      const isOverdue = row.status !== "paid" && new Date(v) < new Date();
      return <span className={isOverdue ? "tx-danger fw-bold" : ""}>{functions.formatDate(v)}{isOverdue ? " ⚠" : ""}</span>;
    }},
    { key: "total_amount", label: "Total", render: (v) => functions.formatCurrency(v) },
    { key: "amount_paid", label: "Paid", render: (v) => <span className="tx-success">{functions.formatCurrency(v)}</span> },
    { key: "balance_due", label: "Balance", render: (v) => <span className={v > 0 ? "tx-danger fw-bold" : "tx-muted"}>{functions.formatCurrency(v)}</span> },
    { key: "status", label: "Status", render: (v) => statusBadge(v) },
    {
      key: "id", label: "Actions",
      render: (id, row) => (
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-light" onClick={() => navigate(`/accounting/invoices/${id}`)} title="View"><i className="fa-solid fa-eye"></i></button>
          {row.status === "draft" && (
            <RenderSecure code="ACC-INVOICE-APPROVE">
              <button className="btn btn-sm btn-success" onClick={() => handleApprove(id)} title="Approve"><i className="fa-solid fa-check"></i></button>
            </RenderSecure>
          )}
          {(row.status === "approved" || row.status === "partial" || row.status === "overdue") && (
            <RenderSecure code="ACC-INVOICE-PAY">
              <button className="btn btn-sm btn-primary" onClick={() => navigate(`/accounting/invoices/${id}/pay`)} title="Record Payment"><i className="fa-solid fa-money-bill-wave"></i></button>
            </RenderSecure>
          )}
          {row.status !== "void" && row.status !== "paid" && (
            <RenderSecure code="ACC-INVOICE-VOID">
              <button className="btn btn-sm btn-outline-danger" onClick={() => handleVoid(id)} title="Void"><i className="fa-solid fa-ban"></i></button>
            </RenderSecure>
          )}
        </div>
      ),
    },
  ];

  const typeOptions = [{ value: "outgoing", label: "Sales (Customer) Invoices" }, { value: "incoming", label: "Purchase (Supplier) Invoices" }];
  const statusOptions = [{ value: "", label: "All Status" }, { value: "draft", label: "Draft" }, { value: "approved", label: "Approved" }, { value: "partial", label: "Partially Paid" }, { value: "paid", label: "Paid" }, { value: "overdue", label: "Overdue" }, { value: "void", label: "Void" }];

  const unpaidTotal = invoices.filter((i) => i.status !== "paid" && i.status !== "void").reduce((s, i) => s + (parseFloat(i.balance_due) || 0), 0);

  return (
    <AppContainer
      title={filterType === "outgoing" ? "Sales Invoices" : "Purchase Invoices"}
      previousPageTitle="Accounting"
      description="Track all customer and supplier invoices. Red due dates indicate overdue invoices. Use 'Record Payment' to apply a payment and automatically post to the ledger."
      actions={[
        { label: filterType === "outgoing" ? "New Sales Invoice" : "New Purchase Invoice", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => navigate(`/accounting/invoices/new?type=${filterType}`) },
        { label: "Aging Report", icon: "fa-solid fa-clock", className: "btn btn-outline-secondary", onClick: () => navigate(`/accounting/invoices/aging?type=${filterType}`) },
      ]}
    >
      <Toaster />

      {unpaidTotal > 0 && (
        <div className="alert alert-warning d-flex align-items-center gap-2 mb-3">
          <i className="fa-solid fa-triangle-exclamation"></i>
          <span>Total outstanding {filterType === "outgoing" ? "receivables" : "payables"}: <strong>{functions.formatCurrency(unpaidTotal)}</strong></span>
        </div>
      )}

      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Invoice Type</label>
            <Select2 options={typeOptions} value={filterType} onChange={setFilterType} />
          </div>
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select2 options={financialYears.map((y) => ({ value: String(y.id), label: y.name }))} value={selectedFY} onChange={setSelectedFY} placeholder="Select Year" />
          </div>
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Status</label>
            <Select2 options={statusOptions} value={filterStatus} onChange={setFilterStatus} placeholder="All Status" />
          </div>
        </div>
      </div>

      <div className="card custom-card p-3">
        <DataTable columns={columns} data={invoices} loading={loading} pageSize={15} emptyMessage="No invoices found." />
      </div>
    </AppContainer>
  );
};

export default Invoices;
